<template>
  <div class="model-tester">
    <el-card>
      <template #header>
        <div class="card-header">
          <el-icon><Monitor /></el-icon>
          <span>开放模型评测</span>
        </div>
      </template>

      <div class="intro">
        <p>上传您自己训练的模型（.pth 权重文件），即可在标准验证集上获得详细性能报告。</p>
        <p>要求：模型结构必须与 <code>CaptchaModel</code> 完全一致（<a href="/templates/captcha_model_template.py" download>点击下载模板</a>）。</p>
        <p>文件限制：≤200MB，仅支持 .pth 格式。</p>
      </div>

      <el-upload
          class="upload-area"
          drag
          action="#"
          :auto-upload="false"
          :on-change="handleFileChange"
          :file-list="fileList"
          :limit="1"
          accept=".pth"
      >
        <el-icon class="el-icon--upload"><upload-filled /></el-icon>
        <div class="el-upload__text">将模型文件拖到此处或 <em>点击上传</em></div>
      </el-upload>

      <div v-if="uploadId" class="progress-area">
        <el-steps :active="stepActive" finish-status="success" align-center>
          <el-step title="上传" :description="uploadStepDesc" />
          <el-step title="评测" :description="evalStepDesc" />
          <el-step title="完成" />
        </el-steps>
        <div v-if="stepActive === 1" class="button-area">
          <el-button type="primary" @click="handleStartEvaluation" :loading="evalLoading">开始评测</el-button>
        </div>
        <div v-if="stepActive === 3" class="button-area">
          <el-button type="info" @click="reset">重新上传</el-button>
          <el-button type="primary" @click="showDetailDialog = true">查看详细分析</el-button>
        </div>
      </div>

      <!-- 结果显示 -->
      <div v-if="resultData" class="result-area">
        <h3>评测结果</h3>
        <p>整体准确率：{{ overallAccuracy }}%</p>
      </div>
    </el-card>

    <!-- 详细分析对话框 -->
    <model-analysis-dialog
        v-model:visible="showDetailDialog"
        :result-data="resultData"
    />
  </div>
</template>

<script setup>
import { ref } from 'vue'
import { ElMessage } from 'element-plus'
import { UploadFilled, Monitor } from '@element-plus/icons-vue'
import { uploadModel, startEvaluation as apiStartEvaluation, getEvaluationResult } from '@/api/captcha'
import ModelAnalysisDialog from '@/components/ModelAnalysisDialog.vue'

const fileList = ref([])
const uploadId = ref('')
const stepActive = ref(0) // 0: 未上传, 1: 已上传待评测, 2: 评测中, 3: 完成
const uploadStepDesc = ref('')
const evalStepDesc = ref('')
const evalLoading = ref(false)
const resultData = ref(null)
const overallAccuracy = ref('')
const showDetailDialog = ref(false)

const handleFileChange = (file) => {
  const formData = new FormData()
  formData.append('file', file.raw)
  uploadModel(formData).then(resp => {
    if (resp.data.code === 200) {
      uploadId.value = resp.data.data.uploadId
      stepActive.value = 1
      uploadStepDesc.value = '上传成功'
      ElMessage.success('上传成功，请点击开始评测')
    } else {
      ElMessage.error(resp.data.msg || '上传失败')
    }
  }).catch(() => {
    ElMessage.error('上传请求失败')
  })
}

const handleStartEvaluation = () => {
  evalLoading.value = true
  apiStartEvaluation(uploadId.value).then(resp => {
    console.log('启动评测响应:', resp)
    if (resp.data.code === 200) {
      stepActive.value = 2
      evalStepDesc.value = '评测中...'
      ElMessage.success('评测任务已启动')
      pollResult()
    } else {
      ElMessage.error(resp.data.msg || '启动失败')
      evalLoading.value = false
    }
  }).catch(error => {
    console.error('启动评测异常:', error)
    ElMessage.error('请求失败')
    evalLoading.value = false
  })
}

const pollResult = () => {
  const timer = setInterval(() => {
    getEvaluationResult(uploadId.value)
        .then(resp => {
          console.log('轮询响应:', resp)
          if (resp.data.code === 200) {
            const data = resp.data.data
            if (data && data.total_samples !== undefined) {
              resultData.value = data
              const totalSamples = data.total_samples || 0
              const correctCount = data.per_sample ? data.per_sample.filter(s => s.correct).length : 0
              overallAccuracy.value = totalSamples ? ((correctCount / totalSamples) * 100).toFixed(2) : '0.00'
              stepActive.value = 3
              evalStepDesc.value = '评测完成'
              evalLoading.value = false
              clearInterval(timer)
            } else {
              console.log('评测中，当前数据:', data)
            }
          } else {
            console.warn('非200状态码:', resp.data.code, resp.data.msg)
            ElMessage.error(resp.data.msg || '评测失败')
            clearInterval(timer)
            evalLoading.value = false
            stepActive.value = 2
            evalStepDesc.value = '评测失败'
          }
        })
        .catch(error => {
          console.error('轮询请求异常:', error)
          console.error('错误响应:', error.response)
          ElMessage.error('请求失败：' + (error.response?.data?.msg || error.message))
          clearInterval(timer)
          evalLoading.value = false
          stepActive.value = 2
          evalStepDesc.value = '评测失败'
        })
  }, 2000)
}

const reset = () => {
  fileList.value = []
  uploadId.value = ''
  stepActive.value = 0
  uploadStepDesc.value = ''
  evalStepDesc.value = ''
  resultData.value = null
  overallAccuracy.value = ''
  showDetailDialog.value = false
}
</script>

<style scoped>
.model-tester {
  padding: 20px;
  max-width: 800px;
  margin: 0 auto;
}

.intro {
  background-color: #f8f9fa;
  padding: 15px;
  border-radius: 4px;
  margin-bottom: 20px;
}

.upload-area {
  margin-bottom: 20px;
}

.progress-area {
  margin-top: 20px;
}

.button-area {
  margin-top: 20px;
  text-align: center;
}

.result-area {
  margin-top: 30px;
}
</style>